import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  Document,
  TranscriptorEncounterResponse,
} from '@/shared/api/transcriptor/transcriptorApi';

import { getDocumentTabValue } from '../lib/documentTab';

export type EncounterTab =
  | { document: Document; kind: 'document'; value: string }
  | { kind: 'transcript'; value: 'transcript' };

const TAB_QUERY_PARAM = 'tab';
const TRANSCRIPT_TAB: EncounterTab = {
  kind: 'transcript',
  value: 'transcript',
};

const buildTabs = (
  encounter: TranscriptorEncounterResponse,
): EncounterTab[] => {
  const documentTabs: EncounterTab[] = (encounter.documents ?? [])
    .filter((document) => Boolean(getDocumentTabValue(document)))
    .map((document) => ({
      document,
      kind: 'document' as const,
      value: getDocumentTabValue(document),
    }));

  return [...documentTabs, TRANSCRIPT_TAB];
};

export const useEncounterTabs = (
  encounter: TranscriptorEncounterResponse | null,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo<EncounterTab[]>(
    () => (encounter ? buildTabs(encounter) : []),
    [encounter],
  );
  const urlTabValue = searchParams.get(TAB_QUERY_PARAM);
  const defaultTab = tabs[0];
  const activeTab = tabs.find((tab) => tab.value === urlTabValue) ?? defaultTab;

  const handleTabChange = (tab: EncounterTab) => {
    const next = new URLSearchParams(searchParams);

    if (tab.value === defaultTab?.value) {
      next.delete(TAB_QUERY_PARAM);
    } else {
      next.set(TAB_QUERY_PARAM, tab.value);
    }

    setSearchParams(next, { replace: true });
  };

  return {
    activeTab,
    tabs,
    onTabChange: handleTabChange,
  };
};
